// @ts-check
// Module core/figure
// Handles figures in the document.
// Adds width and height to images, if they are missing.
// Generates a Table of Figures wherever there is a #tof element.

import {
  addId,
  getIntlData,
  getPreviousSections,
  renameElement,
  showWarning,
  wrapInner,
} from "./utils.js";
import { html } from "./import-maps.js";

export const name = "core/figures";

const localizationStrings = {
  en: {
    list_of_figures: "List of Figures",
    fig: "Figure ",
  },
  ja: {
    fig: "図 ",
    list_of_figures: "図のリスト",
  },
  ko: {
    fig: "그림 ",
    list_of_figures: "그림 목록",
  },
  nl: {
    fig: "Figuur ",
    list_of_figures: "Lijst met figuren",
  },
  es: {
    fig: "Figura ",
    list_of_figures: "Lista de Figuras",
  },
  fr: {
    list_of_figures: "Liste des figures",
    fig: "Figure ",
  },
  zh: {
    fig: "图 ",
    list_of_figures: "规范中包含的图",
  },
  de: {
    fig: "Abbildung",
    list_of_figures: "Abbildungsverzeichnis",
  },
};

const l10n = getIntlData(localizationStrings);

export function run() {
  const tof = collectFigures();

  // Create a Table of Figures if a section with id 'tof' exists.
  const tofElement = document.getElementById("tof");
  if (tof.length && tofElement) {
    decorateTableOfFigures(tofElement);
    tofElement.append(
      html`<h1>${l10n.list_of_figures}</h1>`,
      html`<ul class="tof">
        ${tof}
      </ul>`
    );
  }
}

/**
 * process all figures
 */
function collectFigures() {
  /** @type {HTMLElement[]} */
  const tof = [];
  document.querySelectorAll("figure").forEach((fig, i) => {
    const caption = fig.querySelector("figcaption");

    if (caption) {
      decorateFigure(fig, caption, i);
      tof.push(getTableOfFiguresListItem(fig.id, caption));
    } else {
      const msg = "Found a `<figure>` without a `<figcaption>`.";
      showWarning(msg, name, { elements: [fig] });
    }
  });
  return tof;
}

/**
 * @param {HTMLElement} figure
 * @param {HTMLElement} caption
 * @param {number} i
 */
function decorateFigure(figure, caption, i) {
  const title = caption.textContent;
  addId(figure, "fig", title);
  // set proper caption title
  wrapInner(caption, html`<span class="fig-title"></span>`);
  caption.prepend(
    html`<a class="self-link" href="#${figure.id}"
      >${l10n.fig}<bdi class="figno">${i + 1}</bdi></a
    >`,
    " "
  );
}

/**
 * @param {string} figureId
 * @param {HTMLElement} caption
 * @return {HTMLElement}
 */
function getTableOfFiguresListItem(figureId, caption) {
  const tofCaption = caption.cloneNode(true);
  tofCaption.querySelectorAll("a").forEach(anchor => {
    renameElement(anchor, "span").removeAttribute("href");
  });
  return html`<li class="tofline">
    <a class="tocxref" href="${`#${figureId}`}">${tofCaption.childNodes}</a>
  </li>`;
}

/**
 * if it has a parent section, don't touch it
 * if it has a class of appendix or introductory, don't touch it
 * if all the preceding section siblings are introductory, make it introductory
 * if there is a preceding section sibling which is an appendix, make it appendix
 * @param {Element} tofElement
 */
function decorateTableOfFigures(tofElement) {
  if (
    tofElement.classList.contains("appendix") ||
    tofElement.classList.contains("introductory") ||
    tofElement.closest("section")
  ) {
    return;
  }

  const previousSections = getPreviousSections(tofElement);
  if (previousSections.every(sec => sec.classList.contains("introductory"))) {
    tofElement.classList.add("introductory");
  } else if (previousSections.some(sec => sec.classList.contains("appendix"))) {
    tofElement.classList.add("appendix");
  }
}
